import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Header from "@/components/Header";
import { toast } from "sonner";
import { Bell, Check, X, BellRing } from "lucide-react";

const SPORTS = [
  { value: "Lacrosse", enabled: true },
  { value: "Football", enabled: false },
  { value: "Basketball", enabled: false },
  { value: "Baseball", enabled: false },
  { value: "Soccer", enabled: false },
  { value: "Volleyball", enabled: false },
];
const GENDERS = ["Men", "Women", "Coed"];
const LEVELS = ["MS", "HS", "JUCO", "College"];
const DIVISIONS = ["D1", "D2", "D3", "NAIA", "NJCAA", "MCLA D1", "MCLA D2", "WCLA", "NCLA", "Club", "HS Varsity", "JV", "Freshman", "Middle School"];
const REGIONS = ["Northeast", "Southeast", "Upper Midwest", "Lower Midwest", "Northwest", "Southwest"];
const SETTINGS = ["Public", "Private"];
const SALARY_BANDS = ["0-5,000", "5,000-10,000", "10,000-25,000", "25,000-50,000", "50,000-75,000", "75,000-100,000", "100,000-200,000", "200,000+"];

export default function Alerts() {
  const [prefs, setPrefs] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [scanBusy, setScanBusy] = useState(false);

  const load = async () => {
    const [p, a] = await Promise.all([
      api.get("/coach/alert-preferences"),
      api.get("/coach/alerts"),
    ]);
    setPrefs(p.data);
    setAlerts(a.data);
  };

  useEffect(() => { load(); }, []);

  const toggle = (key, value) => {
    const arr = prefs[key] || [];
    setPrefs({ ...prefs, [key]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value] });
  };
  const u = (k, v) => setPrefs({ ...prefs, [k]: v });

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/coach/alert-preferences", prefs);
      setPrefs(data);
      toast.success("Alert preferences saved.");
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const scan = async () => {
    setScanBusy(true);
    try {
      const { data } = await api.post("/coach/scan-alerts");
      await load();
      toast.success(`${data.new_alerts} new alert${data.new_alerts === 1 ? "" : "s"} created.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Scan failed"); }
    finally { setScanBusy(false); }
  };

  const dismiss = async (id) => {
    await api.post(`/coach/alerts/${id}/dismiss`);
    await load();
  };

  if (!prefs) return <Shell><div className="p-10 heading text-3xl">LOADING…</div></Shell>;

  return (
    <Shell>
      <div className="px-4 md:px-8 lg:px-12 py-8" data-testid="alerts-page">
        <div className="text-xs tracking-widest text-volt">CONSENT-FIRST ALERTS</div>
        <h1 className="heading text-4xl md:text-5xl mt-2">JOB ALERTS</h1>
        <p className="text-neutral-400 mt-2 max-w-2xl">We notify you when a vacancy matches your criteria. You decide whether to express interest. No program ever sees you for a job you haven't opted into.</p>
        <Link to="/coach/alerts/email" data-testid="email-alert-prefs-link" className="inline-block mt-3 text-xs underline underline-offset-4 text-volt">Email alerts (daily digest / weekly alert) — manage or stop →</Link>

        {/* Preferences */}
        <div className="mt-8 border border-neutral-800 p-6 bg-appcard" data-testid="alert-prefs">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-xs tracking-widest text-volt">YOUR CRITERIA</div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="alerts-enabled" checked={prefs.enabled} onChange={(e) => u("enabled", e.target.checked)} />
              <span className="uppercase tracking-widest text-xs">{prefs.enabled ? "Alerts ON" : "Alerts OFF"}</span>
            </label>
          </div>
          <Group title="Sport">{SPORTS.map((s) => <Chip key={s.value} disabled={!s.enabled} on={prefs.sports.includes(s.value)} onClick={() => s.enabled && toggle("sports", s.value)} testid={`chip-sport-${s.value}`}>{s.value}{s.enabled ? "" : " · soon"}</Chip>)}</Group>
          <Group title="Gender">{GENDERS.map((s) => <Chip key={s} on={prefs.genders.includes(s)} onClick={() => toggle("genders", s)} testid={`chip-gender-${s}`}>{s}</Chip>)}</Group>
          <Group title="Level">{LEVELS.map((s) => <Chip key={s} on={prefs.levels.includes(s)} onClick={() => toggle("levels", s)} testid={`chip-level-${s}`}>{s}</Chip>)}</Group>
          <Group title="Division">{DIVISIONS.map((s) => <Chip key={s} on={prefs.divisions.includes(s)} onClick={() => toggle("divisions", s)} testid={`chip-div-${s}`}>{s}</Chip>)}</Group>
          <Group title="Region">{REGIONS.map((s) => <Chip key={s} on={prefs.regions.includes(s)} onClick={() => toggle("regions", s)} testid={`chip-region-${s.replace(/\s/g,'-')}`}>{s}</Chip>)}</Group>
          <Group title="Setting">{SETTINGS.map((s) => <Chip key={s} on={prefs.settings.includes(s)} onClick={() => toggle("settings", s)} testid={`chip-setting-${s}`}>{s}</Chip>)}</Group>
          <Group title="Minimum salary band">
            <select className="bg-appcard border border-neutral-800 px-3 py-2 text-sm" data-testid="min-salary" value={prefs.min_salary_band || ""} onChange={(e) => u("min_salary_band", e.target.value || null)}>
              <option value="">Any</option>
              {SALARY_BANDS.map((s) => <option key={s} value={s}>${s}</option>)}
            </select>
          </Group>
          <Group title="Minimum match score">
            <input type="number" min="40" max="100" className="bg-appcard border border-neutral-800 px-3 py-2 text-sm w-24" data-testid="min-score" value={prefs.min_score || 65} onChange={(e) => u("min_score", parseInt(e.target.value || 65))} />
          </Group>
          <div className="flex items-center gap-3 mt-6 flex-wrap">
            <button onClick={save} disabled={busy} data-testid="save-prefs-btn" className="bg-blaze text-white heading text-base px-5 py-2.5 hover:glow-blaze transition-all disabled:opacity-50">{busy ? "SAVING…" : "SAVE CRITERIA"}</button>
            <button onClick={scan} disabled={scanBusy} data-testid="scan-alerts-btn" className="border border-volt text-volt heading text-base px-5 py-2.5 hover:bg-volt hover:text-white transition-all disabled:opacity-50 flex items-center gap-2">
              <BellRing size={16} /> {scanBusy ? "SCANNING…" : "SCAN NOW"}
            </button>
          </div>
        </div>

        {/* Alerts feed */}
        <div className="mt-10">
          <div className="text-xs tracking-widest text-blaze">YOUR ALERTS</div>
          <h2 className="heading text-3xl mt-1">RECENT MATCHES</h2>
          {alerts.length === 0 ? (
            <div className="mt-4 border border-dashed border-neutral-800 p-10 text-neutral-500">No alerts yet. Set your criteria above and click SCAN NOW.</div>
          ) : (
            <div className="mt-4 space-y-3">
              {alerts.map((a) => (
                <div key={a.id} className={`border ${a.status === "dismissed" ? "border-neutral-900 opacity-50" : "border-neutral-800"} bg-appcard p-5`} data-testid={`alert-${a.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-start gap-4">
                      <div className="heading text-3xl text-mono text-blaze min-w-[60px]">{a.score}</div>
                      <div>
                        <Link to={`/jobs/${a.job_id}`} data-testid={`alert-job-link-${a.id}`} className="heading text-xl hover:text-blaze">{a.job_title}</Link>
                        <div className="text-neutral-400 text-sm">{a.program_name}</div>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {(a.reasons || []).map((r, i) => <span key={i} className="text-[10px] tracking-widest border border-neutral-800 px-2 py-0.5 text-neutral-400">{r}</span>)}
                        </div>
                        <div className="text-mono text-[10px] text-neutral-600 mt-2">{new Date(a.created_at).toLocaleString()}</div>
                      </div>
                    </div>
                    {a.status !== "dismissed" && (
                      <button onClick={() => dismiss(a.id)} data-testid={`dismiss-${a.id}`} className="text-neutral-500 hover:text-blaze flex items-center gap-1 text-xs uppercase tracking-widest"><X size={14} /> Dismiss</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
}

function Group({ title, children }) {
  return (
    <div className="mt-4">
      <div className="text-[10px] tracking-widest text-neutral-500 mb-2">{title.toUpperCase()}</div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}
function Chip({ on, onClick, testid, children, disabled }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick} disabled={disabled} className={`px-3 py-1.5 text-xs uppercase tracking-widest border transition-colors ${disabled ? "border-neutral-800 text-neutral-600 cursor-not-allowed" : on ? "bg-blaze border-blaze text-white" : "border-neutral-700 text-neutral-300 hover:border-volt"}`}>{children}</button>
  );
}
function Shell({ children }) { return <div className="min-h-screen bg-appbg text-white"><Header />{children}</div>; }
